import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'antd';
import './index.scss';

import { textContent } from '../../../types/data.global';
import { logoSVG } from '../../WhalesLabPage/whales';
import LabFooter from '../../WhalesLabPage/LabFooter';
import { usePalette } from '../../../theme/PaletteContext';





// references
// 1. Manling Li: https://hackmd.io/@zAmnkcJDS5CHhVb-PzcMPA/S15pslRn0
// 2. Fumeng Yang: https://fig-x.github.io/workwithus
// 3. Zhutian: https://chenzhutian.org/prospectives
// 4. Qianwen: https://qianwen.info/pages/work_with_me/#if-you-are-considering-applying-for-phd, https://qianwen.info/pages/work_with_me/phd/


const FORM_URL = 'https://forms.gle/mFEhhzz1g8dG4MnNA';

const RecruitingPage: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const emailAddress = 'yifang.wang@fsu.edu';
  const intro = textContent.workWithMePage.introduction;
  const { theme } = usePalette();
  const markSVG = useMemo(() => logoSVG('mark', theme.dark ? 'dark' : 'light'), [theme]);

  // interaction: copy email to clipboard
  const copyEmailToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(emailAddress);
      setIsModalVisible(true);
    } catch (err) {
      Modal.error({
        title: 'Copy Failed',
        content: 'Failed to copy email to clipboard.',
        centered: true,
      });
    }
  };
  const handleModalOk = () => {
    setIsModalVisible(false);
  };

  // reveal on scroll
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const els = rootRef.current ? Array.from(rootRef.current.querySelectorAll('.rv')) : [];
    const io = new IntersectionObserver(es => es.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
    }), { rootMargin: '0px 0px -8% 0px' });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  const formLink = <a href={FORM_URL} target='_blank' rel='noreferrer' className='clickable-form-link'>form</a>;
  const emailLink = <span className='clickable-email' onClick={copyEmailToClipboard}>Email</span>;
  const keepShort = <p className='keep-short'>Note: please keep the email <span>short</span>.</p>;

  // positions and how to apply
  const positions = [
    {
      title: 'Prospective Ph.D. Students',
      when: 'Fall 2027',
      subject: 'Prospective Ph.D. -- Your Full Name (Current University)',
      details: null,
      step2: <>noting that you have filled out the form, and attach any additional materials that support your application that are not included in the form.</>,
    },
    {
      title: 'Research Interns',
      when: 'Graduate and Undergraduate, Long-Term Available',
      subject: 'Prospective Intern -- Your Full Name (Current University)',
      details: <ul>
                 <li>The internship can be in-person or remote.</li>
                 <li>You could be either a current Master's student, an undergraduate student in FSU or other universities.</li>
                 <li>We will provide multiple ideas for interns to choose from, also very supportive if interns want to pursue their own ideas.</li>
               </ul>,
      step2: <>noting that you have filled out the form, and <span className='difference-highlight'>listing your project interests</span>. We will contact you if it is a good match.</>,
    },
    {
      title: 'Visiting Students and Scholars',
      when: 'PhD students etc.',
      subject: 'Visiting Scholar -- Your Full Name (Current University)',
      details: <p>We also welcome visiting students and scholars (PhD students etc.) to visit our research group (remotely and in-person are both welcome).</p>,
      step2: <>noting that you have filled out the form, and <span className='difference-highlight'>listing your project interests</span>. We will contact you if it is a good match.</>,
    },
  ];

  const chips = (items: string[]) => items.map((item, index) => (
    <React.Fragment key={index}><span className='chip'>{item}</span>{index < items.length - 1 && ', '}</React.Fragment>
  ));


  return (
    <div className='whales-page whales-join' ref={rootRef}>

      {/* ============ Join us ============ */}
      <section className='blk join-hero'>
        <span className='join-bg' aria-hidden='true' dangerouslySetInnerHTML={{ __html: markSVG }} />
        <div className='wrap'>
          <p className='eyebrow'><b>Join us</b> Positions · Fall 2027</p>
          <h1 className='h2 jp-h'>Come explore with us.</h1>
          <p className='lede'>We are recruiting Ph.D. students (multiple positions for Fall 2027), research interns and visiting scholars. Backgrounds in AI agents, generative AI, AI for science or data visualization are especially welcome — and so is curiosity that crosses disciplines.</p>
          <div className='jp-cta'>
            <a className='btn sun' href={FORM_URL} target='_blank' rel='noreferrer'>Application form ↗</a>
            <button type='button' className='btn line' onClick={copyEmailToClipboard} title={`Copy ${emailAddress}`}>Email Yifang</button>
            <a className='btn line' href='#positions' onClick={(e) => { e.preventDefault(); document.getElementById('positions')?.scrollIntoView({ behavior: 'smooth' }); }}>How to apply ↓</a>
          </div>
          <div className='what'>
            <div><b>What we look for</b>Strong programming skills and solid mathematical foundations, self-motivation, and a genuine passion for research.</div>
            <div><b>How we work</b>Rigorous interdisciplinary research published across VIS, HCI and general-audience venues — and presented to computational social science communities, as well as interdisciplinary venues (IC2S2, ICSSI).</div>
            <div><b>What you will gain</b>Close one-on-one mentoring, hands-on experience building human–AI systems end to end, and support to publish, present at top venues and grow your own research agenda.</div>
          </div>
        </div>
      </section>

      {/* ============ Work with me ============ */}
      <section className='blk' id='workWithMe'>
        <div className='wrap'>
          <div className='rv'>
            <h2 className='eyebrow'><b>01</b> Work with us</h2>
            <p className='wwm-lead'>{intro.overview}</p>
          </div>
          <dl className='wwm'>
            <div className='wwm-row rv'>
              <dt>Research Areas</dt>
              <dd>
                <p>
                  {intro.researchAreas.description[0]} {chips(intro.researchAreas.list)}.{' '}
                  {intro.researchAreas.description[1]}{' '}
                  {intro.researchAreas.description[2]}
                </p>
                <p className='colored-text'>{intro.researchAreas.description[3]}</p>
              </dd>
            </div>
            <div className='wwm-row rv'>
              <dt>Publication Venues</dt>
              <dd>
                <p>
                  As an interdisciplinary research team, we publish across multiple disciplines: in visualization and HCI venues
                  such as {chips(intro.publicationVenues.cs)}, and in general-audience journals including {chips(intro.publicationVenues.generalAudience)}.
                  Our work has also been invited for presentation at conferences such as {chips(intro.publicationVenues.css)}.
                </p>
                <p>Overall, our lab is dedicated to cultivating interdisciplinary researchers who bridge computing and the social sciences with rigor and depth, and whose work speaks to both technical and broader scientific communities.</p>
              </dd>
            </div>
            <div className='wwm-row rv'>
              <dt>Requirements</dt>
              <dd>
                <p>{intro.studentRequirements.require}</p>
                <p className='plus'><b>Plus+</b>{intro.studentRequirements.plus}</p>
              </dd>
            </div>
            <div className='wwm-row rv'>
              <dt>Collaborations</dt>
              <dd>
                <p>
                  We have a strong network of collaborators from various disciplines, institutions, and social sectors, including but not limited to Harvard/Northwestern Kellogg/Columbia/HKUST/ZJU/… and industry collaborators like Microsoft/Bytedance Seed/…
                  Excellent candidates may have opportunities to collaborate with professors from top universities and researchers in industries.
                  Outstanding interns can be recommended for exchange visits or pursue higher degrees to top schools or internships and jobs at top company research institutes.
                </p>
              </dd>
            </div>
          </dl>
        </div>
      </section>

      {/* ============ Positions ============ */}
      <section className='blk' id='positions'>
        <div className='wrap'>
          <div className='rv'>
            <h2 className='eyebrow'><b>02</b> Positions</h2>
            <p className='lede pos-lede'>If our research interests align, there is a strong likelihood for a fruitful and synergistic collaboration. Here is the list of positions and how to contact us.</p>
            <p className='pos-note'>We usually check recruitment-related emails once a week and receive many applications, so we may not be able to respond to each individually. If our research interests align, we will reach out. Thank you for your understanding and patience.</p>
          </div>
          <div className='grid'>
            {positions.map(p => (
              <article className='pos-card rv' key={p.title}>
                <div>
                  <h3>{p.title}</h3>
                  <p className='when'>{p.when}</p>
                </div>
                <div className='position-details'>
                  <div className='strong-text'>Required Actions</div>
                  <ol>
                    <li>Fill in this {formLink}.</li>
                    <li>{emailLink} Yifang with the subject <code>{p.subject}</code>, {p.step2}</li>
                  </ol>
                </div>
                {keepShort}
                {p.details && (
                  <div className='position-details'>
                    <div className='strong-text'>Position Details</div>
                    {p.details}
                  </div>
                )}
                <div className='acts'>
                  <a className='btn sun' href={FORM_URL} target='_blank' rel='noreferrer'>Application form</a>
                  <a className='btn line' href={`mailto:${emailAddress}?subject=${encodeURIComponent(p.subject)}`}>Email</a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <LabFooter />

      {/* <MyPanel viewTitle="Why FSU?" width={'100%'} height={'auto'} panelHeaderHeight={72}>
        <div className='why-fsu-div'>
          {textContent.workWithMePage.whyFSU}
        </div>
      </MyPanel> */}

      <Modal
        title={null}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalOk}
        closable={false}
        footer={null}
        className="email-copy-modal"
        style={{ top: 50 }}
        width={300}
        transitionName=""
      >
        <div className="modal-content">
          <div className="modal-message">Copied: {emailAddress}</div>
        </div>
      </Modal>
    </div>
  )
}

export default RecruitingPage;
